/**
 *  [2022] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * :ZNOTE:
 * tag: cacheManage
 * note:
 *  - cacheManageの本体
 * sample:
 *  - ほぼ全てのプロジェクト
 * :ZNOTE:
 */

/**
 * DynamoDBの1つのテーブルに対応するキャッシュオブジェクト
 *
 * 達成したいこと
 *  - 更新があった情報だけを取得できるようにし、テーブルの全スキャン＆ダウンロードを極力なくす
 *  - テーブル構成に依存しない、汎用的なクラスとして実装する
 *
 * 方針
 *  - IndexedDBはサーバのキャッシュとして使う
 *  - IndexedDBはputに時間がかかる（indexing）ので、メモリ上でも別途データを管理する
 *  - エントリの削除は、deletedフラグを立てるだけ、または完全削除の２種類。基本的には完全削除はサーバ側で判断するものとし、クライアントからは行わない
 *  - 強制的に全スキャン＆ダウンロードする仕組みも用意する
 *
 * 前提条件
 *  - プライマリーキー (string)、セカンダリーキー、およびupdatedAt (number)、がテーブルに設定されていること
 *  - DynamoDBと同じようにハッシュキー（プライマリーキー）とレンジキー（セカンダリーキー）が設定されていて、かつupdatedAtをレンジキーとするLSIが設定されているものとする
 *  - deletedキーが追加できること
 *
 * 使い方
 *  - アプリケーションロジック内で、DynamoDBテーブルに対応するCacheManageオブジェクトをインスタンス化する
 *  - DBにデータを書き込むor更新するときは、DBとCacheManageオブジェクトの両方に書き込む
 *  - DBからデータを削除したいときは、DBおよびCacheManageオブジェクトの該当エントリにdeletedフラグを立てて登録する
 *  - データ１件を取得したい場合は、まずCacheManageオブジェクトに問い合わせる。返答がundefinedだった場合はDBに問い合わせる。返ってきた情報はCacheManageオブジェクトに登録する
 *  - データを一括取得したい場合は、まずCacheManageオブジェクトに最新エントリの時刻を問い合わせ、それをDBに投げてその時刻以降の情報だけを取得する。返ってきた情報はCacheManageオブジェクトに登録する
 */

/**
 * Version 2 (2023/12/30)
 *  * put()およびbulkPut()の処理を変更し、サーバ側での排他制御（必ず先勝ちになるようにするため）とエラーハンドリングができるようにする
 *    - 書き込み処理と登録・更新済みのデータの取得を分離する
 *    - エラーを取り出せるようにする
 *    - サーバ側（cachemanage_base.py）には、排他制御とfreshnessを入れる。cacheManageClass.tsにもfreshnessを入れる
 */

import {Dexie} from 'dexie';
import {Base64} from 'js-base64';
import pako  from 'pako';
import AsyncLock from 'async-lock';

import {BaseDbStructure, TMediaInfo, TRecordWithMedia} from './interfaces';
import {InMemoryCache} from './inMemoryCache';
import {CacheManageMedia} from './cacheManageMedia';
import {DB_VERSION} from './cacheManageInitializer';
import {CacheManageTimestamp} from './cacheManageTimestamp';
import {printStackTrace} from '@zettant/commonUtils';
import {IErrorMessage} from '../logger/errorMessages';


const AVOID_ARCHIVE_PREFIX = '::&';   // Lambdaのcachemanage_base.pyと同じ値にすること!!!

class CacheManage {
  private debugPrint = false;
  private testMode = false;  // ダミードライバおよびlocalstackを使う時などにfreshnessのチェックを一部スキップする

  protected readonly db: BaseDbStructure;
  protected readonly initializer?: (tableName: string, initParams: any, version?: number)=>any;
  protected readonly initParams: any;
  protected readonly tableName: string;
  protected readonly _primaryKeyName: string;
  protected readonly _secondaryKeyName: any;
  protected dataCache: InMemoryCache;
  protected timestampManage: CacheManageTimestamp;
  protected mediaManage: CacheManageMedia;
  protected lock = new AsyncLock();
  protected lastAccess: {[key:string]: number} = {};  // getByTime()でサーバにアクセスした時刻（あまりにも短い時間間隔でアクセスしてしまうことを防ぐ）
  protected lastReceivedForAvoidDup: {[key:string]: (string|number)[]} = {};  // getByTime()は直前に受信したメッセージの最終時刻と同時刻のものを次も受信する仕様なので、アプリ側にそれを伝えないようにするためにメモを置いておく
  protected toleranceOfGetByTime = 0;
  protected removeLeadTime = 604800;  // 7*86400s
  protected deleteLocalEntryDuration = 0;  // 負の値の場合に限りremoveAtが設定されたら直ちにローカルのエントリを削除する。0以上であれば設定されているremoveAtの時刻からdeleteLocalEntryDurationの時間だけ前になったら削除する

  public getMedia: (record: any, fileName: string)=>Promise<Uint8Array|undefined>;  // 後方互換性を取るため（直接CacheManageのメソッドを呼ぶアプリがある）
  public updateMedia: (record: any, fileName: string, media: Uint8Array)=>Promise<boolean>;
  public bulkUpdateMedia: (dat: {record: any, fileName: string, media: Uint8Array}[])=>Promise<boolean>;
  public deleteMedia: (record: any, fileName: string)=>Promise<boolean>;

  public createAdapter = async (value: any, overwrite=true, errorObj?:IErrorMessage) => 0;  // put()時（データ新規登録）のサーバへのアクセス関数で上書きする
  public updateAdapter = async (value: any, errorObj?:IErrorMessage) => 0;  // put()時（データ更新および削除）のサーバへのアクセス関数で上書きする
  public bulkCreateAdapter = async (valuesB64: string, errorObj?:IErrorMessage) => 0;  // put()時（データ新規登録）のサーバへのアクセス関数で上書きする
  public bulkUpdateAdapter = async (valuesB64: string, errorObj?:IErrorMessage) => 0;  // put()時（データ新規登録）のサーバへのアクセス関数で上書きする
  public deleteAdapter = async (value: any): Promise<any> => {value.deleted = true; value.updatedAt = (new Date()).getTime(); return value;};  // delete()時（データ削除）のサーバへのアクセス関数で上書きする
  public readAdapter = async (primaryKey: string, timestamp=0): Promise<any> => [1];  // get()時のサーバへのアクセス関数で上書きする
  public readAdapter2 = async (primaryKey: string, timestamp=0): Promise<any> => [1];  // readAdapterで返却されるデータを圧縮するバージョン（今後これを主流にする９
  public readAllAdapter = async (): Promise<any> => [];  // scanAll()時のサーバへのアクセス関数で上書きする
  public putPrimaryArchiveAdapter = async (value: any): Promise<any> => {};
  public rawPutAdapter = async (records: any): Promise<any> => true;
  public flushForTestingAdapter = async (): Promise<any> => {};
  public archiveForSimpleTestingAdapter = async (record: any): Promise<any> => {};

  get primaryKeyName(): string {
    return this._primaryKeyName;
  }
  get secondaryKeyName(): string {
    return this._secondaryKeyName;
  }

  public getDb = () => this.db;
  public setLatestTimestampInfo = (allInfo: {[key:string]: number}) => this.timestampManage.setLatest(allInfo);
  public setTolerance = (val: number) => this.toleranceOfGetByTime = val;
  public setRemoveLeadTime = (val: number) => this.removeLeadTime = val;
  public setDeleteLocalEntryDuration = (val: number) => this.deleteLocalEntryDuration = val;
  protected shouldBeRemoved = (record: any): boolean => {
    // EXP: すでに消えていてアプリケーション側に返答すべきでない場合はtrueを返す
    if (!record.removeAt || record.removeAt < 0) return false;
    if (record.deleted || this.deleteLocalEntryDuration < 0) return true;
    // EXP 現在時刻が、削除予定時刻-deleteLocalEntryDurationを超えている場合はtrue（つまり、アプリには返答しない）
    return record.removeAt - this.deleteLocalEntryDuration < Math.floor(new Date().getTime()/1000);  // removeAtはDynamoDBのTTLになるので秒単位
  };
  public setTestMode = (val: boolean) => this.testMode = val;

  /**
   * アダプターをセットする
   *   ダミードライバを用いてローカルテストをしたり、機能拡張して別のサーバに繋いだりできるようにするために、
   *   アクセスメソッド（＝アダプタ）を差し替えられるようにしている
   * @param createAdapter
   * @param updateAdapter
   * @param bulkCreateAdapter
   * @param bulkUpdateAdapter
   * @param deleteAdapter
   * @param readAdapter
   * @param readAdapter2
   * @param readAllAdapter
   * @param putMediaAdapter
   * @param bulkPutMediaAdapter
   * @param getMediaAdapter
   * @param deleteMediaAdapter
   * @param putPrimaryArchiveAdapter
   * @param rawPutAdapter
   * @param flushForTestingAdapter
   * @param archiveForTestingAdapter
   */
  public setup({createAdapter, updateAdapter, bulkCreateAdapter, bulkUpdateAdapter, deleteAdapter, readAdapter, readAdapter2, readAllAdapter, putMediaAdapter, bulkPutMediaAdapter, getMediaAdapter, deleteMediaAdapter, putPrimaryArchiveAdapter, rawPutAdapter, flushForTestingAdapter, archiveForTestingAdapter}: any) {
    if (createAdapter) this.createAdapter = createAdapter;
    if (updateAdapter) this.updateAdapter = updateAdapter;
    if (bulkCreateAdapter) this.bulkCreateAdapter = bulkCreateAdapter;
    if (bulkUpdateAdapter) this.bulkUpdateAdapter = bulkUpdateAdapter;
    if (deleteAdapter) this.deleteAdapter = deleteAdapter;
    if (readAdapter) this.readAdapter = readAdapter;
    if (readAdapter2) this.readAdapter2 = readAdapter2;
    if (readAllAdapter) this.readAllAdapter = readAllAdapter;
    if (putMediaAdapter) this.mediaManage.putMediaAdapter = putMediaAdapter;
    if (bulkPutMediaAdapter) this.mediaManage.bulkPutMediaAdapter = bulkPutMediaAdapter;
    if (getMediaAdapter) this.mediaManage.getMediaAdapter = getMediaAdapter;
    if (deleteMediaAdapter) this.mediaManage.deleteMediaAdapter = deleteMediaAdapter;
    if (putPrimaryArchiveAdapter) this.putPrimaryArchiveAdapter = putPrimaryArchiveAdapter;
    if (rawPutAdapter) this.rawPutAdapter = rawPutAdapter;
    if (flushForTestingAdapter) this.flushForTestingAdapter = flushForTestingAdapter;
    if (archiveForTestingAdapter) this.archiveForSimpleTestingAdapter = archiveForTestingAdapter;
  }

  /**
   * コンストラクタ
   * @param tableName {string}
   * @param primaryKeyName {string}
   * @param secondaryKeyName {string}
   * @param initializer {(initParams: any,, version?: number)=>any}
   * @param initParams {any}
   * @param version {number}
   */
  constructor(tableName: string, primaryKeyName: string, secondaryKeyName: string, initializer: (tableName: string, initParams: any, version?: number)=>any, initParams: any, version=DB_VERSION) {
    this.tableName = tableName;
    this._primaryKeyName = primaryKeyName;
    this._secondaryKeyName = secondaryKeyName;
    this.initializer = initializer;
    this.initParams = initParams;
    this.db = initializer(tableName, initParams, version);  // this.tableをメインで使うが、サーバとの通信を避けるためのキャッシュとして使う。

    this.dataCache = new InMemoryCache(secondaryKeyName, this.getFromIndexedDb);
    this.timestampManage = new CacheManageTimestamp(this.db.meta, this._primaryKeyName);
    this.mediaManage = new CacheManageMedia(this.db.media, this.dataCache, this._primaryKeyName, this._secondaryKeyName, this.putDataForMembers); // dbを作成した後に初期化する必要がある
    this.getMedia = this.mediaManage.getMedia;
    this.updateMedia = this.mediaManage.updateMedia;
    this.bulkUpdateMedia = this.mediaManage.bulkUpdateMedia;
    this.deleteMedia = this.mediaManage.deleteMedia;

    if ((this.db.entries as any).setClearCallback) {
      // localStorageを用いる場合、容量オーバーが比較的頻繁に発生する可能性があるので、clear時にlatestもクリアする
      (this.db.entries as any).setClearCallback(() => this.dataCache.clear());
    }
  }

  /**
   * primaryKeyを指定して該当する全てのレコードをIndexedDbから取得するユーティリティ
   *   arrow関数で定義しないと、InMemoryCacheオブジェクトから正しくアクセスできない（this.dbが未定義になる）
   * @param primaryKeyValue {string}
   * @protected
   */
  public getFromIndexedDb = (primaryKeyValue: string): Promise<any> =>
    this.db.entries.where([this.primaryKeyName, this.secondaryKeyName]).between([primaryKeyValue, Dexie.minKey], [primaryKeyValue, Dexie.maxKey], true, true).toArray();

  /**
   * arrow関数で定義しないと、CacheManageMediaオブジェクトから正しくアクセスできない（this.dbが未定義になる）
   * @param primaryKeyValue
   * @param datToRegister
   */
  public putDataForMembers = async(primaryKeyValue: string, datToRegister: any) => await this.putData(primaryKeyValue, datToRegister);

  /**
   * IndexedDBに書き込む（引数はarrayでも単体でもOK）
   * @param dat {any}
   * @protected
   */
  protected async putInIndexedDb(dat: any) {
    if (Array.isArray(dat)) {
      await this.db.entries.bulkPut(dat);
    } else {
      await this.db.entries.put(dat);
    }
  }

  /**
   * IndexedDBとメモリの両方にデータを書き込む
   * @param primaryKeyValue {string}
   * @param datToRegister {any}
   * @protected
   */
  protected async putData(primaryKeyValue: string, datToRegister: any) {
    await Promise.all([
      this.dataCache.updateInMemory(primaryKeyValue, datToRegister),
      this.putInIndexedDb(datToRegister),
      this.timestampManage.registerLatest()
    ]);
  }

  public async getEntry(primaryKeyValue: string, secondaryKeyValue:string): Promise<any> {
    const res = await this.dataCache.getInMemory(primaryKeyValue, secondaryKeyValue);
    if (!res) return res;
    return JSON.parse(JSON.stringify(res));
  }

  /**
   * 最新時刻を巻き戻す（不具合が起こったときのため）
   * @param primaryKeyValue {string}
   * @param timeToRewind {number}
   */
  public rewindTimestamp = async (primaryKeyValue: string, timeToRewind: number) => this.timestampManage.rewindTimestamp(primaryKeyValue, timeToRewind);

  /**
   * 最新時刻を強制設定する
   * @param primaryKeyValue {string}
   * @param timestamp_millisec {number}
   */
  public setLatestTimestamp = async (primaryKeyValue: string, timestamp_millisec: number) => {
    console.info('***setLatestTimestamp:', primaryKeyValue, timestamp_millisec);
    await this.timestampManage.setLatestTimestamp(primaryKeyValue, timestamp_millisec);
  };

  public getLatestTimestamp = (primaryKeyValue: string) => this.timestampManage.getLatest(primaryKeyValue);

  /**
   * IndexedDBをすべてスキャンして、this.latestを再構築する
   *  (テスト用にpublicにしているが、本来はprivateでいい)
   * @private
   */
  public checkAllLatestTimestamps = async (allEntries: any) => this.timestampManage.checkAllLatestTimestamps(allEntries);

  /**
   * 通常のレコード登録・更新処理
   * @param value
   * @param media
   * @param errorObj {IErrorMessage} エラー発生時のエラー取得用
   */
  public put(value: any, media?: TMediaInfo, errorObj?:IErrorMessage) {
    return this._put(value, media, errorObj);
  }

  /**
   * レコードの更新のみを行う（新規登録はしない）
   * @param value
   * @param media
   * @param errorObj {IErrorMessage} エラー発生時のエラー取得用
   */
  public putUpdateOnly(value: any, media?: TMediaInfo, errorObj?:IErrorMessage) {
    return this._put(value, media, errorObj, true);
  }

  /**
   * エントリを登録または更新する。サーバにも登録・更新する
   * [設計変更]
   * create()、update()でレコードは返さず、getByTime()するようにする
   * create()やupdate()の返答は登録・更新の成否のみとする
   *
   * @param value {any}
   * @param media {TMediaInfo}
   * @param errorObj {IErrorMessage} エラー発生時のエラー取得用
   * @param updateOnly {boolean} 既存レコードの更新のみ許可
   */
  private async _put(value: any, media?: TMediaInfo, errorObj?:IErrorMessage, updateOnly=false) {
    let entry: any;
    let oldestUpdatedAt = 0; // older recordのエラーが起こった時に、更新対象となる最も古いupdatedAtの1ms前まで戻す

    if (media) {
      if (!value.dataPaths) value.dataPaths = {} as any;
      for (const m in media) {  // EXP: 新しく追加/変更するmediaを指定する。既登録メディアはそのまま保持される
        value.dataPaths[m] = ['', 0, media[m].length];  // [ファイル名, ファイルパス, 作成時刻, サイズ]
      }
    }

    // OBJ: '--'で始まるHashkeyのレコードは、IndexedDBに保存しないし、更新もしない（書き込みのみ）。かつ自分のputではlatestも更新しない。
    // EXP: 下記if文は'--'以外のものに対しての処理。
    if (!value[this._primaryKeyName].startsWith('--')) {
      entry = await this.getEntry(value[this._primaryKeyName], value[this._secondaryKeyName]);
    }

    let updatedAt: number|undefined;
    if (!entry || entry.deleted) {
      // EXP: 新規登録（最初にDBに登録し、updatedAtをサーバに埋めてもらう）
      if (updateOnly) {
        // 既存レコードの更新のみ許可されている場合は何もしない
        if (errorObj) {
          errorObj.code = 5;
          errorObj.msg = '既存レコードの更新のみ許可されています';
        }
        return;
      }
      if (!entry) {
        // deleteしたものではなく、完全に新規作成の場合はfreshnessを0にする
        value.__freshness = 0;
      } else {
        // deleteしたものを復活させる場合はfreshnessを更新しないと同時アクセスが発生した場合にサーバで先勝ち処理が実施できない
        value.__freshness = (entry.__freshness || 0) + 1;
      }
      const overwrite = entry && entry.deleted;
      updatedAt = await this.createAdapter(value, overwrite, errorObj);
    }
    else {
      // 変更レコード(同じprimaryKey, secondaryKeyのレコードが存在する)
      if (oldestUpdatedAt === 0 || oldestUpdatedAt > entry.updatedAt) oldestUpdatedAt = entry.updatedAt;  // bulkPutする中で、キャッシュされている一番古いupdatedAtを記録しておく

      // OBJ: 削除したものと同じHashkey,Rangekeyの情報を登録しようとした場合は、削除フラグやremoveAtを取り消す必要がある
      if (entry.removeAt && entry.removeAt !== -1 && (!value.removeAt || value.removeAt === -1)) {
        value.removeAt = -1;
        if (entry.deleted) value.deleted = false;  // DynamoDBのupdate関数ではカラムの削除ができないのでfalseをあえて設定する必要がある
      } else if (entry.deleted && !value.deleted) {
        value.deleted = false;
      }

      // freshnessを更新しなければ登録・更新できない
      if (typeof entry.__freshness === 'undefined') {
        value.__freshness = 0;
      } else if (entry.__freshness === value.__freshness || value.__freshness === -1) {  // 同時編集が発生しない、または起こったとしても大きな問題にならないことが確信できる場合は-1も指定して良い
        // キャッシュの内容と同じものを編集して新しいレコードを作ったことを確認するために、entryとvalのfreshnessが同じであることを求める
        value.__freshness = entry.__freshness + 1;
      } else {
        //console.log('****** cacheManage put() older or invalid record:', value[this._primaryKeyName], value[this._secondaryKeyName], `inCache:${entry.__freshness} <=> new:${value.__freshness}`);
        // テストする時はここをコメントアウト
        // TODO: サーバ側をテストする時は、ここでエラーを発生させずにそのまま通過させる（freshnessはテストコードで適当に設定しておく）、
        if (errorObj) {
          errorObj.code = 5;
          errorObj.msg = 'older or invalid record';
          await this.setLatestTimestamp(value[this._primaryKeyName], oldestUpdatedAt-1);  // older recordになった場合、何度試してもキャッシュが不適切であるために復活できなくなる可能性があるので、巻き戻す必要がある。ただし全部キャッシュクリアする必要はない。
        }
        return;
      }

      // EXP: 更新（最初にDBに登録し、updatedAtをサーバに埋めてもらう）
      updatedAt = await this.updateAdapter(value, errorObj);
    }
    if (!updatedAt) {
      if (errorObj) {
        if (errorObj.msg.toLowerCase().includes('older record')) {
          // 既に古いレコードがある場合は、最も古いupdatedAtの1ms前まで戻す
          await this.setLatestTimestamp(value[this._primaryKeyName], oldestUpdatedAt - 1);
        }
        errorObj.code = 5;
        errorObj.msg = 'Cannot create/update';  // TODO: createとupdateを区別する（createの場合はcode=5, updateの場合はcode=6）
      }
      return;
    }

    const newRecords = await this._getByTime(value[this._primaryKeyName], updatedAt-1, false, true);  // アクセス直後ではあるが必ずサーバに確認する
    if (newRecords.length === 0) return;

    const record = newRecords.filter((r) => r[this._primaryKeyName] === value[this._primaryKeyName] && r[this._secondaryKeyName] === value[this._secondaryKeyName])[0];
    if (!record) return;

    // OBJ: '--'で始まるHashkeyのレコードは、IndexedDBに保存しないし、更新もしない（書き込みのみ）。かつ自分のputではlatestも更新しない。また、dataPathsも無視する。
    if (record[this.primaryKeyName].startsWith('--')) {
      return record;
    }

    // EXP: 以降はprimaryKeyが'--'では始まっていないもののみの処理

    if (record.dataPaths) value.dataPaths = JSON.parse(JSON.stringify(record.dataPaths));

    // EXP: media情報が含まれている場合、パスが返ってくるので、MEDIAPUTでメディアファイルをS3にアップロードする
    if (media && value.dataPaths) {
      await this.mediaManage.registerMediaInfoForSingleRecord(value, media);
    }
    return record;
  }

  /**
   * 複数のレコードを一括で登録する（mediaなしバージョン）
   * @param records {any[]}
   * @param errorObj {IErrorMessage}
   */
  public async bulkPut(records: any[], errorObj?:IErrorMessage) {
    return this.bulkPutWithMedia(records.map((r) => ({record: r})), errorObj);
  }

  /**
   * 複数のレコードを一括で登録する（mediaありバージョン）
   * 含まれるレコードのprimaryKeyの値は必ず1種類とする。
   * @param valueWithMedia {{record: any, media?: TMediaInfo}[]}
   * @param errorObj {IErrorMessage}
   */
  public async bulkPutWithMedia(valueWithMedia: TRecordWithMedia[], errorObj?:IErrorMessage) {
    const entriesToCreate: {[dataId:string]: TRecordWithMedia} = {};
    const entriesToUpdate: {[dataId:string]: TRecordWithMedia} = {};
    const primaryKey: string = valueWithMedia[0].record[this._primaryKeyName];
    let oldestUpdatedAt = 0; // older recordのエラーが起こった時に、更新対象となる最も古いupdatedAtの1ms前まで戻す

    // レコードごとにメディアに関する情報の作成と、IndexedDBの登録済み情報をチェックして新規・上書きを仕分ける
    for (const val of valueWithMedia) {
      if (primaryKey !== val.record[this._primaryKeyName]) {
        if (errorObj) {
          errorObj.code = 999;
          errorObj.msg = 'primaryKey must be just one in bulkPut';
        }
        return;
      }
      if (val.media) {
        // メディア情報がある場合は、後でアップロードするために必要なデータ構造をrecordの中に作っておく
        if (!val.record.dataPaths) val.record.dataPaths = {} as any;
        for (const m in val.media) {
          val.record.dataPaths[m] = ['', 0, val.media[m].length];
        }
      }

      // キャッシュされているレコードを調べる
      const entry = await this.getEntry(val.record[this._primaryKeyName], val.record[this._secondaryKeyName]);
      if (!entry || entry.deleted) {
        // 新規登録レコード
        val.record.__freshness = 0;
        entriesToCreate[val.record[this._secondaryKeyName]] = val;
      }
      else {
        // 変更レコード(同じprimaryKey, secondaryKeyのレコードが存在する)
        if (oldestUpdatedAt === 0 || oldestUpdatedAt > entry.updatedAt) oldestUpdatedAt = entry.updatedAt;  // bulkPutする中で、キャッシュされている一番古いupdatedAtを記録しておく

        // OBJ: 削除したものと同じHashkey,Rangekeyの情報を登録しようとした場合は、削除フラグやremoveAtを取り消す必要がある
        if (entry.removeAt && entry.removeAt !== -1 && (!val.record.removeAt || val.record.removeAt === -1)) {
          val.record.removeAt = -1;
          if (entry.deleted) val.record.deleted = false;
        } else if (entry.deleted && !val.record.deleted) {
          val.record.deleted = false;
        }

        // freshnessを更新しなければ登録・更新できない
        if (typeof entry.__freshness === 'undefined') {  // 存在しないときはとりあえず0を入れる
          val.record.__freshness = 0;
        } else if (entry.__freshness === val.record.__freshness || val.record.__freshness === -1) {  // 同時編集が発生しない、または起こったとしても大きな問題にならないことが確信できる場合は-1も指定して良い
          // キャッシュの内容と同じものを編集して新しいレコードを作ったことを確認するために、entryとvalのfreshnessが同じであることを求める
          val.record.__freshness = entry.__freshness + 1;
        } else {
          //console.log('****** cacheManage bulkPut() older or invalid record:', val.record[this._primaryKeyName], val.record[this._secondaryKeyName], `inCache:${entry.__freshness} <=> new:${val.record.__freshness}`);
          if (!this.testMode) {
            // テストする時はここをは通さない
            // TODO: サーバ側をテストする時は、ここでエラーを発生させずにそのまま通過させる（freshnessはテストコードで適当に設定しておく）。この下の方にもコメントアウトすべき行がある
            if (errorObj) {
              errorObj.code = 5;
              errorObj.msg = 'older or invalid record';
              await this.setLatestTimestamp(primaryKey, oldestUpdatedAt-1);  // older recordになった場合、何度試してもキャッシュが不適切であるために復活できなくなる可能性があるので、巻き戻す必要がある。ただし全部キャッシュクリアする必要はない。
            }
            return;
          }
        }
        entriesToUpdate[val.record[this._secondaryKeyName]] = val;
      }
    }

    // OBJ: サーバにアップロードする。新規追加分と更新分を分けてアップロードする
    let updatedAt: number|undefined;
    if (Object.keys(entriesToCreate).length > 0) {
      // 新規登録レコードのアップロード
      const toUpload = Base64.fromUint8Array(pako.deflate(JSON.stringify(Object.values(entriesToCreate).map((ent) => ent.record))));
      const u = await this.bulkCreateAdapter(toUpload, errorObj);
      if (!u) {
        if (errorObj) {
          if (errorObj.msg.toLowerCase().includes('older record')) {
            // 既に古いレコードがある場合は、最も古いupdatedAtの1ms前まで戻す
            await this.setLatestTimestamp(primaryKey, oldestUpdatedAt-1);
          }
          errorObj.code = 6;
          errorObj.msg = 'Cannot create';
        }
        return;
      }
      if (!updatedAt || u < updatedAt) updatedAt = u;
    }
    if (Object.keys(entriesToUpdate).length > 0) {
      // 更新レコードのアップロード
      const toUpload = Base64.fromUint8Array(pako.deflate(JSON.stringify(Object.values(entriesToUpdate).map((ent) => ent.record))));
      const u = await this.bulkUpdateAdapter(toUpload, errorObj);
      if (!u) {
        if (errorObj) {
          console.error('*** bulkUpdateAdapter error:', errorObj);
          if (errorObj.msg.toLowerCase().includes('older record')) {
            // 既に古いレコードがある場合は、最も古いupdatedAtの1ms前まで戻す
            await this.setLatestTimestamp(primaryKey, oldestUpdatedAt-1);
          }
          errorObj.code = 5;
          errorObj.msg = 'Cannot update';
        }
        return;
      }
      if (!updatedAt || u < updatedAt) updatedAt = u;
    }

    // サーバから登録済みの最新レコードを取得する
    const resp = await this._getByTime(primaryKey, (updatedAt || 1)-1, false, true);  // アクセス直後ではあるが必ずサーバに確認する
    if (resp.length === 0) return;

    // メディアアップロードが必要なら実施する。ついでに戻り値となる登録済みレコード群を作成する
    const results: any = [];
    const promises: any = [];
    for (const r of resp) {
      let origRecord: any;
      if (r[this._secondaryKeyName] in entriesToCreate) {
        origRecord = entriesToCreate[r[this._secondaryKeyName]];
      } else if (r[this._secondaryKeyName] in entriesToUpdate) {
        origRecord = entriesToUpdate[r[this._secondaryKeyName]];
      }
      if (!origRecord) continue;

      if (r.dataPaths) origRecord.record.dataPaths = JSON.parse(JSON.stringify(r.dataPaths));

      // EXP: media情報が含まれている場合、パスが返ってくるので、MEDIAPUTでメディアファイルをS3にアップロードする
      if (origRecord.media && r.dataPaths) {
        if (!this.testMode) {
          promises.push(this.mediaManage.registerMediaInfoForSingleRecord(r, origRecord.media));
        } else {
          // localstackでテストする時、promise.allを使うとエラーになるので、ここでは使わない
          await this.mediaManage.registerMediaInfoForSingleRecord(r, origRecord.media);
        }
      }
      results.push(r);
    }
    if (!this.testMode) await Promise.all(promises);  // localstackでテストする時、promise.allを使うとエラーになるのでスキップ
    return results;
  }

  /**
   * 指定したprimaryKeyのレコード群をアーカイブとしてサーバに登録する
   * その際、すでにサーバ側に同一のアーカイブがあれば、サーバ側でそれを展開しマージしてアーカイブそのものを更新する
   * @param primaryKey
   * @param records
   */
  public async putPrimaryArchive(primaryKey: string, records: any[]) {
    if (records.some((ent) => ent[this._primaryKeyName] !== primaryKey)) return false;  // 全て同じprimaryKeyであることが前提

    const archRecord = {
      [this._primaryKeyName]: primaryKey,
      [this._secondaryKeyName]: `${primaryKey}_archive`,
      __archivedEntries: Base64.fromUint8Array(pako.deflate(JSON.stringify(records))),  // サーバに送るデータはzlib圧縮してBase64にする
      updatedAt: 0,
    };

    const updatedAt = await this.putPrimaryArchiveAdapter(archRecord);
    if (!updatedAt) return false;
    await this._getByTime(primaryKey, updatedAt-1);
    return true;
  }

  public async removeWithoutDelFlag(primaryKeyValue: string, secondaryKeyValue: any, info?: any, errorObj?: IErrorMessage): Promise<boolean> {
    return this._remove(false, primaryKeyValue, secondaryKeyValue, info, errorObj);
  }

  public async remove(primaryKeyValue: string, secondaryKeyValue: any, info?: any, errorObj?: IErrorMessage): Promise<boolean> {
    return this._remove(true, primaryKeyValue, secondaryKeyValue, info, errorObj);
  }

  public async bulkRemove(entries: any[], withoutDelFlag=false, errorObj?: IErrorMessage) {
    const records: any = await Promise.all(entries.map((e) => this.getEntry(e[this._primaryKeyName], e[this._secondaryKeyName])));
    if (!records || records.length === 0) {
      if (errorObj) {
        errorObj.code = 5;
        errorObj.msg = 'Cannot update';
      }
      return;
    }
    const removeAt = Math.floor(new Date().getTime()/1000 + this.removeLeadTime);  // EXP: removeAtはDynamoDBのTTLに用いるため秒単位でなければならない
    const recordToUpdate: any[] = records.filter((e) => e).map((e) => {
      const record = {
        [this._primaryKeyName]: e[this._primaryKeyName],
        [this._secondaryKeyName]: e[this._secondaryKeyName],
        removeAt,
        deleted: !withoutDelFlag,
        __freshness: e.__freshness || 0,
      };
      if (typeof e.timeOfRecord !== 'undefined') record.timeOfRecord = e.timeOfRecord;  // timeBasedDataManageを想定
      return {record};
    });

    if (recordToUpdate.length === 0) {
      if (errorObj) {
        errorObj.code = 5;
        errorObj.msg = 'Cannot update';
      }
      return;
    }
    return await this.bulkPutWithMedia(recordToUpdate, errorObj);
  }

  /**
   * 削除したことを表す情報をレコードにセットする（他のクライアントにも削除が伝わるように）
   * @param withFlag {boolean}
   * @param primaryKeyValue {string}
   * @param secondaryKeyValue {string}
   * @param info {any}
   * @param errorObj {IErrorMessage}
   */
  private async _remove (withFlag: boolean, primaryKeyValue: string, secondaryKeyValue: any, info={}, errorObj?: IErrorMessage): Promise<boolean> {
    let entry: any = await this.getEntry(primaryKeyValue, secondaryKeyValue);
    if (!entry) {
      if (errorObj) {
        errorObj.code = 5;
        errorObj.msg = 'Cannot update';
      }
      return false;
    }

    const removeAt = Math.floor((new Date().getTime()/1000) + this.removeLeadTime);  // EXP: removeAtはDynamoDBのTTLに用いるため秒単位でなければならない
    let recordToUpdate: any = {
      [this._primaryKeyName]: primaryKeyValue,
      [this._secondaryKeyName]: secondaryKeyValue,
      removeAt,
    };
    if (typeof entry.timeOfRecord !== 'undefined') recordToUpdate.timeOfRecord = entry.timeOfRecord;  // timeBasedDataManageを想定
    if (withFlag) recordToUpdate.deleted = true;
    if (info) recordToUpdate = {...recordToUpdate, ...info};
    const updatedAt = await this.updateAdapter(recordToUpdate, errorObj);  // EXP: もともとupdateAdapterを使っていたが、アーカイブされているエントリを削除する場合、レコードがDynamoDBに登録されているとは限らないのでcreateにした
    if (!updatedAt) return false;
    await this._getByTime(primaryKeyValue, updatedAt-1);
    return true;
  }

  /**
   * サーバおよびローカルの情報を同時に削除する
   *
   * @param primaryKeyValue {string}
   * @param secondaryKeyValue {string}
   */
  public async forceDelete(primaryKeyValue: string, secondaryKeyValue?: any): Promise<boolean> {
    const res = await this.getEntry(primaryKeyValue, secondaryKeyValue);
    if (!res) return false;
    await this.dataCache.deleteInMemory(primaryKeyValue, secondaryKeyValue); // ローカルの方は持ってても仕方ないので削除する
    await Promise.all([
      this.db.entries.delete([primaryKeyValue, secondaryKeyValue]),
      this.deleteAdapter({[this._primaryKeyName]:primaryKeyValue, [this._secondaryKeyName]:secondaryKeyValue}),
      this.mediaManage.deleteAllRelatedMedia(res),
    ]);
    return true;
  }

  /**
   * 指定されたprimaryKeyに紐づく全ての情報をローカルから削除する
   * TODO: mediaキャッシュを削除する?
   * @param primaryKeyValue {string}
   * @protected
   */
  public async bulkDeleteLocal(primaryKeyValue: string) {
    // EXP: in-memoryから削除する
    this.dataCache.deleteInMemory(primaryKeyValue);

    // EXP: IndexedDBから削除する
    const entries = await this.db.entries.where([this.primaryKeyName, this.secondaryKeyName]).between([primaryKeyValue, Dexie.minKey], [primaryKeyValue, Dexie.maxKey], true, true).toArray();
    await this.db.entries.bulkDelete(entries.map((e: any) => [primaryKeyValue, e.dataId]));

    // EXP: latestのタイムスタンプを削除する
    await this.timestampManage.remove(primaryKeyValue);
  }

  /**
   * 指定されたprimaryKeyとsecondaryKeyValueに紐づく情報をローカルから削除する
   * TODO: mediaキャッシュを削除する?
   * @param primaryKeyValue {string}
   * @param secondaryKeyValue {string}
   * @protected
   */
  protected async deleteLocal(primaryKeyValue: string, secondaryKeyValue: string) {
    // EXP: in-memoryから削除する
    this.dataCache.deleteInMemory(primaryKeyValue, secondaryKeyValue);

    // EXP: IndexedDBから削除する
    await this.db.entries.delete([primaryKeyValue, secondaryKeyValue]);
  }

  /**
   * アーカイブ機能を利用して、指定されたprimaryKeyの全てのレコードを無効化したレコード群をアーカイブとして登録することで、実質的に削除したのと同じ状態にする
   * 本当に削除されるのはLambdaのCronで定期アーカイブするタイミングである。
   * @param primaryKeyValue
   */
  public async allDeleteUsingArchive(primaryKeyValue: string) {
    const records = await this.getByTime(primaryKeyValue);
    const removeAt = Math.floor((new Date().getTime()/1000) + this.removeLeadTime);  // EXP: removeAtはDynamoDBのTTLに用いるため秒単位でなければならない
    const delRecs: any = [];
    records.forEach((r) => {
      delRecs.push({
        [this.primaryKeyName]: primaryKeyValue,
        [this.secondaryKeyName]: r[this._secondaryKeyName],
        deleted: true,
        removeAt
      });
    });
    await this.putPrimaryArchive(primaryKeyValue, delRecs);
  }

  /**
   * 指定されたprimaryKeyに対するエントリ群を返答する。
   * サーバにアクセスせず、ローカルの情報だけを検索する。確実に同期していると言える場合にだけ利用すること
   *
   * @param primaryKeyValue
   */
  public localGetAll(primaryKeyValue: string): any {
    return JSON.parse(JSON.stringify((this.dataCache.getSortedArray(primaryKeyValue))));
  }

  /**
   * サーバからのレスポンスに基づいて、ローカルのキャッシュや最新時刻のタイムスタンプを更新する
   * @param primaryKeyValue {string}
   * @param latest {number}
   * @param retDatB64 {string}
   */
  public async updateLocalDbWithServerResponse(primaryKeyValue: string, latest: number, retDatB64: string) {
    let result: any[] = JSON.parse(pako.inflate(Base64.toUint8Array(retDatB64), {to: 'string'}));
    const latestInResult = Math.max(result.map((r) => r.updatedAt) as any);
    if (!result || result.length === 0 || latest === latestInResult) {
      this.timestampManage.update(primaryKeyValue, latest+1);  // 何も更新がなかったということなので、とりあえず1ミリ秒進めておいて今後の重複受信を防ぐ
      await this.timestampManage.registerLatest();
      this.lastReceivedForAvoidDup[primaryKeyValue] = [];
      return [];
    }
    // EXP: 前回取得した最新のメッセージと同一時刻のメッセージを受信してしまうため、それを削除するためのメモを適用する
    if (this.lastReceivedForAvoidDup[primaryKeyValue] && this.lastReceivedForAvoidDup[primaryKeyValue].length > 0) {
      result = result.filter((ent) =>
        !this.lastReceivedForAvoidDup[primaryKeyValue].includes(ent[this.secondaryKeyName]) || this.lastReceivedForAvoidDup[primaryKeyValue][0] < ent.updatedAt
      );
    }

    let latestTimestamp = 0;
    result.forEach((ent: any) => {
      if (ent.updatedAt > latestTimestamp) {
        latestTimestamp = ent.updatedAt;
        this.lastReceivedForAvoidDup[primaryKeyValue] = [latestTimestamp, ent[this.secondaryKeyName]];  // 1番目の要素に時刻を入れておく
      } else if (ent.updatedAt === latestTimestamp) {
        this.lastReceivedForAvoidDup[primaryKeyValue].push(ent[this.secondaryKeyName]);
      }
    });
    if (latestTimestamp > 0) {
      this.timestampManage.update(primaryKeyValue, latestTimestamp);
    }

    if (primaryKeyValue.startsWith('--')) {  // '--'で始まるものはIndexedDBにもinMemoryCacheにも保存しない。アーカイブにもならない
      return result;
    }

    if (result.length > 0) {
      await this.putData(primaryKeyValue, result);  // EXP: ローカルのメモリとIndexedDBに書き込む
      await this.dataCache.loadInMemory(primaryKeyValue);
    }
    return result;
  }

  /**
   * _getByTime() へのラッパー（フィルタなし）
   *
   * @param primaryKeyValue {string}
   * @param timestamp {number}
   * @param withDeleted {boolean} trueにすると、deletedフラグのあるものも返答する
   */
  public getByTime = async (primaryKeyValue: string, timestamp=0, withDeleted=false): Promise<any[]> =>
    await this._getByTime(primaryKeyValue, timestamp, withDeleted);

  /**
   * 更新されたもの、削除されたもの(deleteフラグ)を分類して返す
   * @param primaryKeyValue {string}
   */
  public sync = async(primaryKeyValue: string): Promise<{updated: any[], deleted: any[]}> => {
    const res: any = await this.getByTime(primaryKeyValue, -1, true);
    const updated: any = [];
    const deleted: any = [];
    const now = new Date().getTime();
    res.forEach((record) => {
      if (record.deleted || (record.removeAt && record.removeAt < now)) {
        deleted.push(record);
      } else {
        updated.push(record);
      }
    });
    return {updated, deleted};
  };

  /**
   * 指定時刻より後に更新されたデータを取得する（指定時刻の情報は含まないことに注意）
   *
   * このオブジェクトが管理している最新の更新データより後に更新された情報をサーバからとってくる
   * timestampを省略した場合は、全部のデータを返す
   * filterを指定した場合は、サーバ側でそこで指定した値を持つものだけフィルタして返す
   * @param primaryKeyValue {string}
   * @param timestamp {number} -1または最新時刻を指定した時は更新分だけを取得する。0を指定した場合は、全レコードを取得する
   * @param withDeleted {boolean} trueにすると、deletedフラグのあるものも返答する
   * @param force {boolean} trueにすると、かならずサーバにアクセスする（put直後の確認のため）
   */
  protected async _getByTime(primaryKeyValue: string, timestamp=0, withDeleted=false, force=false): Promise<any[]> {
    let res: any = [];
    const onlyNewFlag = timestamp && (timestamp === this.timestampManage.getLatest(primaryKeyValue) || timestamp === -1);  // サーバの更新分だけを取得したい場合(=timestampに最新値がセットされている)はtrue
    if (this.debugPrint) {
      printStackTrace();
      console.log(`[1]------ _getByTime: primaryKeyValue=${primaryKeyValue}, timestamp=${timestamp}, onlyNewFlag=`, onlyNewFlag, this.timestampManage.getLatest(primaryKeyValue));
    }

    // あまりにも頻繁にはアクセスさせないようにする（30ms）
    if (force || !this.lastAccess[primaryKeyValue] || new Date().getTime() -this.lastAccess[primaryKeyValue] > 30) {
      // OBJ: primaryKeyValueごとに排他処理
      await this.lock.acquire(primaryKeyValue, async () => {
        // 未取得のデータを取得する
        // このオブジェクトが管理している最新の更新データより後に更新された情報をサーバからとってくる
        const latest = this.timestampManage.getLatest(primaryKeyValue);
        const ts = latest > this.toleranceOfGetByTime ? latest - this.toleranceOfGetByTime : 0;
        const retDatB64: string = await this.readAdapter2(primaryKeyValue, ts + 1); // サーバから時刻を指定してそれ以降に更新されたデータをとってくる(圧縮されている）
        if (!retDatB64) {
          res = [];
        } else {
          res = await this.updateLocalDbWithServerResponse(primaryKeyValue, latest, retDatB64);
        }
        this.lastAccess[primaryKeyValue] = new Date().getTime();
      });
    } else {
      console.log('cacheManage: server access skipped.');  // TODO: デバッグ用（本格稼働する時は無駄なので削除する）
    }
    if (this.debugPrint) console.log('[2]------ _getByTime: fromServer', res);

    if (this.debugPrint) console.log('[2-2]------ _getByTime');
    if (primaryKeyValue.startsWith('--') || onlyNewFlag) return res;
    if (this.debugPrint) console.log('[2-3]------ _getByTime');

    // EXP: アプリ起動後の初回アクセス時に、IndexedDBからデータをメモリに取り込む
    await this.lock.acquire(primaryKeyValue, async () => {
      if (!this.dataCache.checkKeyInMemory(primaryKeyValue)) {
        await this.dataCache.loadInMemory(primaryKeyValue);
      }
    });

    const candidates = this.dataCache.getSortedArray(primaryKeyValue);
    if (this.debugPrint) console.log('[3]------ _getByTime: candidates', candidates);

    if (withDeleted) {
      // EXP: 指定時刻(timestamp)より後に更新された情報を全て返す(deleted,removeAtなものも含めて全て)
      return JSON.parse(JSON.stringify(candidates.filter((ent: any) => ent.updatedAt > timestamp)));
    } else {
      // EXP: 指定時刻(timestamp)より後に更新された情報かつdeleteフラグが立っていないものだけを返)す
      return JSON.parse(JSON.stringify(candidates.filter((ent: any) => !ent.deleted && !this.shouldBeRemoved(ent) && ent.updatedAt > timestamp)));
    }
  }

  public async get(primaryKeyValue: string, secondaryKeyValue: string): Promise<any> {
    await this.getByTime(primaryKeyValue, this.timestampManage.getLatest(primaryKeyValue));
    const res = await this.dataCache.getInMemory(primaryKeyValue, secondaryKeyValue);
    if (!res) return res;
    return JSON.parse(JSON.stringify(res));
  }

  /**
   * ALLコマンド（scan)を実行する。データをサーバから取得するのみ
   */
  public async scanAll() {
    const retDatB64 = await this.readAllAdapter();
    if (!retDatB64) return;
    return JSON.parse(pako.inflate(Base64.toUint8Array(retDatB64), {to: 'string'}));
  }

  public async rawBulkPut(records: any[]) {
    const compressed = Base64.fromUint8Array(pako.deflate(JSON.stringify(records)));
    return await this.rawPutAdapter(compressed);
  }

  /**
   * deletedフラグがtrueになっているもの、および1ヶ月以上アクセスされていないメディア情報をローカルのIndexedDBから削除する
   *   -> CacheManageを継承したクラスからsuper.purge()を呼ばれることがあるので、arrow関数にはできない
   */
  public async purge() {
    const now = new Date().getTime();
    const promises: any = [];

    // EXP: エントリの中のdataに、そのprimaryKeyに属する全てのレコードが含まれているので、dataの中身をひとつずつ全てチェックする
    const allEntries = await this.db.entries.toArray();
    allEntries.forEach((record) => {
      if (!record.deleted && !this.shouldBeRemoved(record)) return;
      this.dataCache.deleteInMemory(record[this.primaryKeyName], record[this.secondaryKeyName]);
      promises.push(this.mediaManage.deleteAllRelatedMedia(record));
      promises.push(this.db.entries.delete([record[this.primaryKeyName], record[this.secondaryKeyName]]));
    });

    // EXP: 1ヶ月以上アクセスされていないメディアを削除する
    const mediaToDelete = await this.db.media.where('lastAccessedAt').below(now-30*86400000).toArray();
    await Promise.all([
      ...promises,
      this.db.media.bulkDelete(mediaToDelete.map((ent) => ent.filePath))
    ]);

    await this.checkAllLatestTimestamps(await this.rawGet());  // EXP: purgeでlatestがおかしくなるので再構築する
    this.lastReceivedForAvoidDup = {};
  }

  /**
   * ローカルストレージのこのテーブルの項目を削除する。これを実行したらオブジェクトが実質的に無効になるので注意。
   *
   * @param flushMedia {boolean} メディアキャッシュも削除するかどうか
   */
  public async flush(flushMedia=true) {
    if (this.initializer && flushMedia) {
      await this.db.db.delete();
      const db = this.initializer(this.tableName, this.initParams, DB_VERSION);
      await db.meta.clear(); // delete仕切れていない時がある様なので念の為。metaさえ消せばtimestampがリセットされるので、サーバとの再動機ができる
      db.entries.clear();
      db.media.clear();
      this.db.db = db.db;
      this.db.meta = db.meta;
      this.db.entries = db.entries;
      this.db.media = db.media;
    }
    else {
      await this.db.meta.clear();
      await this.db.entries.clear();
      if (flushMedia) await this.db.media.clear();
    }
    this.dataCache = new InMemoryCache(this.secondaryKeyName, this.getFromIndexedDb); // インメモリキャッシュは作り直す
    this.timestampManage = new CacheManageTimestamp(this.db.meta, this.primaryKeyName);
    await this.timestampManage.registerLatest();
    this.mediaManage.reInitialize(this.db.media, this.dataCache);
    this.getMedia = this.mediaManage.getMedia;
    this.updateMedia = this.mediaManage.updateMedia;
    this.bulkUpdateMedia = this.mediaManage.bulkUpdateMedia;
    this.deleteMedia = this.mediaManage.deleteMedia;
    this.lastReceivedForAvoidDup = {};
  }

  /**
   * テスト用の全件取得関数
   */
  public rawGet = async () => this.db.entries.toArray();

  /**
   * テスト用の関数（dev環境でだけLambdaが受け付ける）
   */
  public async flushForTesting () {
    await this.flushForTestingAdapter();
  }

  /**
   * テスト用の関数（dev環境でだけLambdaが受け付ける）
   */
  public async archiveForSimpleTesting (category: string) {
    await this.archiveForSimpleTestingAdapter({category});
  }

  public setDebugPrint(mode=true) {
    console.log('=================== debug log', mode ? 'start': 'end', '=====================');
    this.debugPrint = mode;
  }
}

export {AVOID_ARCHIVE_PREFIX, CacheManage};
